import gql from 'graphql-tag';
import { getGraphQLClientForSpendCubeDataComponentWithToken } from 'utils/apiRequests';
import { allOption } from '../../utils';

function capitalizeFieldNames(arr) {
	return arr.map(obj => {
		const newObj = {};
		for (let key in obj) {
			if (obj.hasOwnProperty(key)) {
				if (
					key === 'pO_BASE_SPEND' ||
					key === 'nET_VALUE_BASE_GBP' ||
					key === 'nET_VALUE_BASE_USD' ||
					key === 'nET_VALUE_BASE_EUR'
				) {
					const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
					newObj[capitalizedKey] = obj[key]?.toLocaleString();
				} else if (key.includes('custom_data')) {
					newObj[key] = obj[key]; // No capitalization for "custom_data" keys
				} else if (key.endsWith('_count')) {
					const newKey = key.slice(0, -6); // Remove the '_count' suffix
					const capitalizedKey =
						newKey.charAt(0).toUpperCase() + newKey.slice(1);
					newObj[capitalizedKey] = obj[key];
				} else {
					if (key === 'sOURCEABLE') {
						const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
						if (obj[key] === true) {
							newObj[capitalizedKey] = 'true';
						} else if (obj[key] === false) {
							newObj[capitalizedKey] = 'false';
						} else {
							newObj[capitalizedKey] = null;
						}
					} else if (key === 'pO_CREATION_DATE') {
						const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
						const formattedDate = new Date(obj[key]).toLocaleDateString(
							'en-GB',
							{ day: '2-digit', month: '2-digit', year: 'numeric' }
						);
						newObj[capitalizedKey] = formattedDate;
					} else {
						const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
						newObj[capitalizedKey] = obj[key];
					}
				}
			}
		}
		return newObj;
	});
}

function getRequiredFilterFormatForCategory(input, type = 2) {
	const prefix = type === 3 ? 'custom_' : 'aP_';
	const parts = input.split('/');
	const filteredParts = parts.filter(part => part.trim() !== '');
	const result = filteredParts.map((value, index) => {
		const categoryKey = `${prefix}L${index + 1}Category`;
		return {
			[categoryKey]: {
				filterType: 'text',
				type: 'equals',
				filter: decodeURIComponent(value),
			},
		};
	});
	return result;
}

const customFilterQuery = (request, columns, newFilters) => {
	const {
		currentSelectCategory,
		isSourceable,
		isDirect,
		taxonomyType,
		dateRangeData,
		searchterm,
		selectedFilter,
	} = newFilters;
	const { categoryName } = currentSelectCategory;
	const { dateFrom, dateTo } = dateRangeData;
	let constructedFilterModel = [
		{
			aptaxonomy: {
				filterType: 'number',
				type: 'equals',
				filter: isDirect ? '1' : '0',
			},
		},
		{
			pO_CREATION_DATE: {
				operator: 'AND',
				filterType: 'date',
				condition1: {
					filterType: 'date',
					type: 'lessThanOrEqual',
					dateFrom: dateTo.split('T')[0],
				},
				condition2: {
					filterType: 'date',
					type: 'greaterThanOrEqual',
					dateFrom: dateFrom.split('T')[0],
				},
			},
		},
	];
	if (isSourceable) {
		constructedFilterModel.push({
			sourceable: {
				filterType: 'number',
				type: 'equals',
				filter: '1',
			},
		});
	}
	if (searchterm === '') {
		constructedFilterModel = constructedFilterModel.filter(
			x => !x[selectedFilter]
		);
	} else {
		constructedFilterModel.push({
			[selectedFilter]: {
				filterType: 'text',
				type: 'contains',
				filter: searchterm,
			},
		});
	}
	if (categoryName !== allOption.id) {
		const categoryFilterModel = getRequiredFilterFormatForCategory(
			categoryName,
			taxonomyType
		);
		constructedFilterModel = [
			...constructedFilterModel,
			...categoryFilterModel,
		];
	}
	const nestedCols = columns
		.filter(col => col.children)
		.map(childrenfields => childrenfields.children)
		.flat()
		.map(col => `${col.field?.charAt(0).toLowerCase()}${col.field?.slice(1)}`);
	const normalcols = columns
		.map(col => `${col.field?.charAt(0).toLowerCase()}${col.field?.slice(1)}`)
		.filter(el => el !== 'undefinedundefined')
		.filter(el => el !== 'pO_ID_AP');
	const fields = normalcols.concat(nestedCols);
	return {
		query: gql`
      query getDataComponents(
          $start: Int!, 
          $end: Int!, 
          $sortModel: [SortModelType!], 
          $rowGroups:[RowGroupModelType!],
          $filterModel:[FilterModelTypeV2!],
		  $valueCols:[ValueColsModelType!],
          $groupCols:[GroupColsModelType!]
        ) {
          getDataComponents(
          startRow: $start,    
          endRow: $end,
          sorting: $sortModel,
          rowGroups:$rowGroups,
          filterModel:$filterModel,
		  valueCols:$valueCols,
          groupCols :$groupCols
        ) {
        ${[...fields]}
        }
      }
    `,
		variables: {
			start: request.startRow,
			end: request.endRow,
			sortModel: [],
			valueCols: request.valueCols,
			rowGroups: [{ id: '1', displayName: fields[0], field: fields[0] }],
			filterModel: constructedFilterModel,
			groupCols: [{ id: '1', displayName: fields[0], field: fields[0] }],
		},
	};
};
const createDatasource = gridOptions => {
	return {
		// called by the grid when more rows are required
		getRows: async params => {
			const object = {};
			object.gridOptions = gridOptions;
			object.client = await getGraphQLClientForSpendCubeDataComponentWithToken();
			const columns = object.gridOptions.columnDefs;

			object.client
				.query(
					customFilterQuery(params.request, columns, gridOptions.newFilters)
				)
				.then(response => {
					let rows = capitalizeFieldNames(response.data.getDataComponents);
					rows.forEach(function(item, index) {
						item.id = index;
					});
					let lastRow = -1;
					if (rows.length <= gridOptions.cacheBlockSize) {
						lastRow = params.request.startRow + rows.length;
					}

					params.successCallback(rows, lastRow);
					let idSequence = 0;
					gridOptions.params.api.forEachNode(function(rowNode, _) {
						rowNode.id = idSequence++;
					});
					gridOptions.params.api.forEachNode(node => {
						if (
							node.data &&
							gridOptions.selectedItems.includes(
								node.data[gridOptions.selectedFilter]
							)
						) {
							node.setSelected(true);
						}
					});
				})
				.catch(err => {
					console.error(err);
					params.failCallback();
				});
		},
	};
};

export default createDatasource;
